import CruciblePiece from './cruciblePiece';
import { MultiplierConfig } from '../config';
import * as config from '../config.json';
import * as THREE from 'three';
import logBaseN from '../logBaseN';
import { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

export class Multiplier extends CruciblePiece {
  protected _config: MultiplierConfig;
  protected _balanceThresholds: number[];
  protected _balanceRingsRadius: number[];
  protected _balanceRings: THREE.Mesh[];
  protected _angle: number;
  protected _speed: number;
  protected _yOffset: number;
  protected _range: number;
  protected _scaledBalance: number;

  constructor() {
    super();
    this._config = config.multiplier;
    this._balanceThresholds = this._config.balanceThresholds;
    this._balanceRingsRadius = [672, 684, 696, 708];
    this._balanceRings = [];
    this._yOffset = 160;
    this._angle = 0;
    this._speed = 0.1;
    this._range = 25;
    this._scaledBalance = 0;
  }

  setup(gltf: GLTF) {
    // Reset group with gltf contents
    this.group = gltf.scene;
    this.group.scale.set(0.02, 0.02, 0.02); // This should be removed with final models

    this.group.traverse((node: any) => {
      if (node.isMesh) {
        node.castShadow = true;
        node.receiveShadow = true;
        if (node.material.map) node.material.map.anisotropy = 16;
      }
    });

    this.group.rotateY(Math.PI / 20);
  }

  animate(timeElapsed: number) {}

  update(totalSubscribedValue: number, circleColor: THREE.Color) {
    this._balanceRings = [];
    this._scaledBalance = logBaseN(2, totalSubscribedValue);

    // Create each ring mesh based on Balance Thresholds
    this._balanceThresholds.forEach((threshold, i) => {
      if (i > 0 && totalSubscribedValue < this._balanceThresholds[i - 1])
        return;

      let previousThreshold = 0;
      if (i > 0) previousThreshold = this._balanceThresholds[i - 1];

      let fillBalance = Math.min(totalSubscribedValue, threshold);

      let fillRatio =
        (fillBalance - previousThreshold) / (threshold - previousThreshold);

      const balanceRingMesh = this._circleMesh(
        this._balanceRingsRadius[i],
        fillRatio,
        circleColor
      );

      const inversedBalanceRingMesh = this._circleMesh(
        this._balanceRingsRadius[i],
        fillRatio,
        circleColor
      );

      inversedBalanceRingMesh.rotateZ(Math.PI);

      this.group.add(balanceRingMesh);
      this._balanceRings.push(balanceRingMesh);

      this.group.add(inversedBalanceRingMesh);
      this._balanceRings.push(inversedBalanceRingMesh);

      // Create small sphere in the end of rings
      const spherePivot = new THREE.Object3D();

      const sphereGeometry = new THREE.SphereGeometry(5, 10, 10);
      const sphereMaterial = new THREE.MeshStandardMaterial({
        emissive: circleColor,
        color: circleColor,
        emissiveIntensity: 1.5
      });

      // If the balance is 0, then don't show the spheres in the end
      if (totalSubscribedValue > 0) {
        const sphereEnd = new THREE.Mesh(sphereGeometry, sphereMaterial);

        sphereEnd.position.z = this._balanceRingsRadius[i];
        sphereEnd.position.y = this._yOffset;

        spherePivot.add(sphereEnd);

        // Rotate pivot to beginning of ring
        spherePivot.rotateY(Math.PI / 2);
        spherePivot.rotateY(-Math.PI / 28);

        // Rotate to the end of the ring
        spherePivot.rotateY(0.975 * Math.PI * fillRatio);

        const inversedSpherePivot = spherePivot.clone(true);
        inversedSpherePivot.rotateY(Math.PI);

        this.group.add(inversedSpherePivot);
        this.group.add(spherePivot);
      }
    });
  }

  _circleMesh(
    meshRadius: number,
    fillRatio: number,
    color: THREE.Color
  ): THREE.Mesh {
    const shapeRadius = 2.8;

    const shape = new THREE.Shape();
    shape.moveTo(0, shapeRadius);
    shape.absarc(0, 0, shapeRadius, 0, 2 * Math.PI, false);
    shape.closePath();

    const extrudedCircle = new THREE.ExtrudeGeometry(shape, {
      steps: 96,
      bevelEnabled: false,
      extrudePath: this._circlePath(meshRadius, 0, 0.975 * Math.PI * fillRatio)
    });

    const circleMaterial = new THREE.MeshStandardMaterial({
      emissive: color,
      color: color,
      emissiveIntensity: 1.5
    });

    const balanceCircle = new THREE.Mesh(extrudedCircle, circleMaterial);

    balanceCircle.position.set(0, this._yOffset, 0);

    balanceCircle.rotateX(Math.PI / 2);
    balanceCircle.rotateZ(Math.PI / 30);

    return balanceCircle;
  }

  _circlePath(radius: number, radiansStart: number, radiansEnd: number) {
    const path = new THREE.Curve<THREE.Vector3>();
    path.getPoint = function (t) {
      // trace the arc as t ranges from 0 to 1
      var segment = (radiansStart - radiansEnd) * t;
      return new THREE.Vector3(
        radius * Math.cos(segment),
        radius * Math.sin(segment),
        0
      );
    };

    return path;
  }

  _createSpline(radius: number, subdivisions: number) {
    const curve = new THREE.EllipseCurve(
      0,
      0,
      radius,
      radius,
      0,
      2 * Math.PI,
      false,
      0
    );

    const points = curve.getPoints(subdivisions);
    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0xffffff });

    const spline = new THREE.Line(geometry, material);

    this.group.add(spline);
    spline.position.set(0, 170, 0);
    spline.rotateX(Math.PI / 2);
  }
}
